/**
 * ConfirmDialog — lightweight reusable confirmation modal (#257 P2-B).
 *
 * Replaces ad-hoc `window.confirm()` calls in user-scoped destructive
 * actions (Settings: revoke key / forget memory / forget all,
 * AdminMemories: delete fact / wipe user). Owner-tier ops in
 * AdminTools / AdminRaffle keep their typed-confirm pattern.
 *
 * Accessibility:
 *   - role="dialog" + aria-modal + aria-labelledby on the wrapper
 *   - Cancel button gets initial focus (safer default for destructive
 *     actions — the user has to take an explicit step to confirm).
 *   - ESC dismisses, backdrop click dismisses.
 *   - Listeners attach on mount via document.addEventListener and
 *     detach on cleanup — CSP-clean, no inline handlers.
 */
import { Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import './ConfirmDialog.css';

let __idCounter = 0;

export default function ConfirmDialog(props) {
  // Stable id for aria-labelledby across renders of this instance.
  const titleId = `v2-confirm-title-${++__idCounter}`;
  let cancelBtn;

  function handleKey(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      props.onCancel?.();
    }
  }

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) props.onCancel?.();
  }

  onMount(() => {
    document.addEventListener('keydown', handleKey, true);
    // Defer focus until after Portal mounts the node.
    queueMicrotask(() => cancelBtn?.focus());
  });
  onCleanup(() => {
    document.removeEventListener('keydown', handleKey, true);
  });

  const confirmClass = () =>
    'btn ' + (props.confirmKind === 'primary' ? 'btn-primary' : 'btn-danger');

  return (
    <Show when={props.open}>
      <Portal>
        <div
          class="v2-confirm__backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={handleBackdrop}
        >
          <div class="v2-confirm">
            <div id={titleId} class="v2-confirm__title">{props.title}</div>
            <Show when={props.body}>
              <div class="v2-confirm__body">{props.body}</div>
            </Show>
            <div class="v2-confirm__actions">
              <button
                type="button"
                class="btn"
                ref={cancelBtn}
                onClick={() => props.onCancel?.()}
              >
                {props.cancelLabel || 'Cancel'}
              </button>
              <button
                type="button"
                class={confirmClass()}
                onClick={() => props.onConfirm?.()}
              >
                {props.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
